import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import { Language, Check } from '@mui/icons-material';
import { UsuarioContext } from '../../providers/userContext';

const languages = [
  { code: 'es', name: 'Castellano', flag: 'ES' },
  { code: 'ca', name: 'Catalá', flag: 'CA' },
  { code: 'eu', name: 'Euskera', flag: 'EU' },
];

// Roles permitidos para cambiar idioma
const ALLOWED_ROLES = ['ADMIN', 'AAFF'];

const LanguageSwitcher = ({ isMobile = false, variant = 'dark', forceShow = false }) => {
  const { i18n, t } = useTranslation('common');
  const user = useContext(UsuarioContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];
  const isLight = variant === 'light' || isMobile;

  // Solo mostrar si forceShow=true (páginas públicas) o si el usuario tiene rol permitido
  const canChangeLanguage = forceShow || (user?.rol && ALLOWED_ROLES.includes(user.rol));

  if (!canChangeLanguage) {
    return null;
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    handleClose();
  };

  return (
    <>
      <Button
        variant="text"
        onClick={handleClick}
        size="small"
        sx={{
          ml: 1,
          px: 1,
          color: isLight ? '#fff' : '#33383b',
          minWidth: 'auto',
        }}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        startIcon={<Language fontSize="small" />}
      >
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline' },
            fontSize: '0.875rem',
          }}
        >
          {currentLanguage.flag}
        </Box>
      </Button>
      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              minWidth: 150,
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            selected={i18n.language === lang.code}
          >
            <ListItemText
              primary={lang.name}
              sx={{ mr: 2 }}
            />
            {i18n.language === lang.code && (
              <ListItemIcon sx={{ minWidth: 'auto' }}>
                <Check fontSize="small" color="primary" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
